/**
 * Context de Autenticação
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authService } from '@services';
import { LoginVendedorRequest, Vendedor } from '@types';
import { SessionUtil, CacheUtil } from '@utils';

interface AuthContextData {
  vendedor: Vendedor | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  canMakePrevisao: boolean;
  canMakePedido: boolean;
  operationType: 'previsao' | 'pedido' | null;
  login: (credentials: LoginVendedorRequest) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextData | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  console.log('[AuthContext] AuthProvider renderizando...');

  const [vendedor, setVendedor] = useState<Vendedor | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Carrega dados da sessão ao inicializar e monitora mudanças
  useEffect(() => {
    const loadSession = () => {
      const sessionData = SessionUtil.getVendedor();

      console.log('[AuthContext] loadSession - dados do localStorage:', {
        sessionData,
        indPedido: sessionData?.indPedido,
        indPedidoType: typeof sessionData?.indPedido,
        indPrevisao: sessionData?.indPrevisao,
        indPrevisaoType: typeof sessionData?.indPrevisao,
      });

      if (sessionData) {
        const vendedorData = {
          id: sessionData.vendId,
          codigo: sessionData.vendCodigo,
          tipo: sessionData.vendTipo,
          nome: sessionData.vendNome,
          email: sessionData.vendEmail,
          pedido: sessionData.indPedido,
          previsao: sessionData.indPrevisao,
          previsaoId: sessionData.indPrevisaoId,
          previsaoInicio: sessionData.indPrevisaoIni,
          previsaoFim: sessionData.indPrevisaoFim,
          previsaoConfirma: sessionData.indPrevisaoConfirma,
        };

        console.log('[AuthContext] vendedorData criado:', {
          pedido: vendedorData.pedido,
          pedidoType: typeof vendedorData.pedido,
          previsao: vendedorData.previsao,
          previsaoType: typeof vendedorData.previsao,
          canMakePrevisao: vendedorData.previsao !== 0 && Number(vendedorData.pedido) === 0,
          canMakePedido: vendedorData.previsao !== 0 && Number(vendedorData.pedido) !== 0,
        });

        setVendedor(vendedorData);
      } else {
        console.log('[AuthContext] Nenhum vendedor encontrado no localStorage');
      }
    };

    // Carrega ao inicializar
    loadSession();

    // Monitora mudanças no localStorage (para detectar login em outra aba ou atualizações)
    const handleStorageChange = (e: StorageEvent) => {
      // Recarrega se alguma chave relacionada ao vendedor mudou
      if (e.key && (e.key.includes('vend') || e.key.includes('ind'))) {
        console.log('[AuthContext] Detectada mudança no localStorage, recarregando sessão...');
        loadSession();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Também monitora mudanças internas com um intervalo curto
    const interval = setInterval(() => {
      const currentSessionData = SessionUtil.getVendedor();
      // Só recarrega se o código do vendedor mudou ou se havia vendedor e agora não há mais
      if (currentSessionData?.vendCodigo !== vendedor?.codigo) {
        console.log('[AuthContext] Detectada mudança interna na sessão, recarregando...');
        loadSession();
      }
    }, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [vendedor?.codigo]);

  const login = useCallback(async (credentials: LoginVendedorRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.login(credentials);

      const vendedorData: Vendedor = {
        id: response.id,
        codigo: response.codigo,
        tipo: response.tipo,
        nome: response.nome,
        email: response.email,
        pedido: response.pedido,
        previsao: response.previsao,
        previsaoId: response.previsaoId,
        previsaoInicio: response.previsao !== 0 ? response.previsao_inicio : undefined,
        previsaoFim: response.previsao !== 0 ? response.previsao_fim : undefined,
        previsaoConfirma: response.previsao !== 0 ? response.previsao_confirma : undefined,
      };

      console.log('[AuthContext] Login bem-sucedido - setVendedor chamado com:', {
        vendedorData,
        previsao: vendedorData.previsao,
        pedido: vendedorData.pedido,
        pedidoType: typeof vendedorData.pedido,
        previsaoType: typeof vendedorData.previsao,
        calculoCanMakePrevisao: vendedorData.previsao !== 0 && Number(vendedorData.pedido) === 0,
        calculoCanMakePedido: vendedorData.previsao !== 0 && Number(vendedorData.pedido) !== 0,
      });

      setVendedor(vendedorData);
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao fazer login';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    // authService.logout() já cuida de preservar o carrinho de previsão se necessário
    authService.logout();
    setVendedor(null);
    setError(null);

    // Limpa cache de produtos ao fazer logout
    CacheUtil.clearAllProdutosCache();
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const isAuthenticated = !!vendedor;

  //Previsao Ativa: indPrevisao <> 0 AND indPedido = 0
  // Converte pedido para número para comparação correta (API pode retornar string ou número)
  // IMPORTANTE: Só calcula se vendedor não for null
  const canMakePrevisao = vendedor !== null && vendedor.previsao !== 0 && Number(vendedor.pedido) === 0;

  //Pedido Ativo: indPrevisao <> 0 AND indPedido <> 0
  // IMPORTANTE: Só calcula se vendedor não for null
  const canMakePedido = vendedor !== null && vendedor.previsao !== 0 && Number(vendedor.pedido) !== 0;

  const operationType = canMakePrevisao
    ? 'previsao'
    : canMakePedido
    ? 'pedido'
    : null;

  // Log sempre que o componente renderiza
  console.log('[AuthContext] Renderizando - Cálculo operationType:', {
    vendedorIsNull: vendedor === null,
    vendedor: vendedor ? {
      previsao: vendedor.previsao,
      previsaoType: typeof vendedor.previsao,
      previsaoIsZero: vendedor.previsao === 0,
      previsaoIsDifferentFromZero: vendedor.previsao !== 0,
      pedido: vendedor.pedido,
      pedidoType: typeof vendedor.pedido,
      pedidoConverted: Number(vendedor.pedido),
      pedidoIsZero: Number(vendedor.pedido) === 0,
      pedidoIsDifferentFromZero: Number(vendedor.pedido) !== 0,
    } : null,
    calculoPrevisao: {
      vendedorNotNull: vendedor !== null,
      previsaoDifferentFromZero: vendedor !== null && vendedor.previsao !== 0,
      pedidoIsZero: vendedor !== null && Number(vendedor.pedido) === 0,
      resultado: canMakePrevisao,
    },
    calculoPedido: {
      vendedorNotNull: vendedor !== null,
      previsaoDifferentFromZero: vendedor !== null && vendedor.previsao !== 0,
      pedidoDifferentFromZero: vendedor !== null && Number(vendedor.pedido) !== 0,
      resultado: canMakePedido,
    },
    canMakePrevisao,
    canMakePedido,
    operationType
  });

  return (
    <AuthContext.Provider
      value={{
        vendedor,
        isAuthenticated,
        isLoading,
        error,
        canMakePrevisao,
        canMakePedido,
        operationType,
        login,
        logout,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  
  return context;
};